import React, { useEffect, useRef } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import '../css/ContactUs.css';
import '../css/about.css';

const DownholeTools = () => {
  const underlineRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (underlineRef.current) {
        underlineRef.current.classList.add('animate');
      }
    }, 50);

    return () => clearTimeout(timer);
  }, []);

  const tools = [
    {
      icon: 'bi bi-record-circle',
      color: '#0c61c0',
      title: 'Concentric Reamers',
      desc: 'Reaming while drilling in a single concentric pass — enlarges the hole behind the bit without a separate trip, keeping the assembly centred and the borehole on gauge.',
    },
    {
      icon: 'bi bi-arrows-angle-expand',
      color: '#dc3545',
      title: 'Under Reamers',
      desc: 'Expandable cutter arms that open the hole below a restriction such as a casing shoe, creating the larger diameter needed for casing and cementing programmes.',
    },
    {
      icon: 'bi bi-bar-chart-steps',
      color: '#fd7e14',
      title: 'Step Drills & Fixed Hole Openers',
      desc: 'Fixed-blade hole openers and step-drill assemblies that drill more than one hole size in a single run, cutting trip time on tapered well designs.',
    },
    {
      icon: 'bi bi-arrows-fullscreen',
      color: '#6f42c1',
      title: 'Expandable Drilling Reamers',
      desc: 'Activated downhole to expand or retract on demand, allowing the wellbore diameter to be adjusted mid-run and the tool to be passed back through restrictions.',
    },
    {
      icon: 'bi bi-gear-wide-connected',
      color: '#28a745',
      title: 'Roller Reamers',
      desc: 'Roller-cutter reamers that enlarge and condition the borehole wall, stabilise the bottom-hole assembly, and reduce torque and drag in deviated sections.',
    },
    {
      icon: 'bi bi-activity',
      color: '#17a2b8',
      title: 'Shock Subs',
      desc: 'Shock absorbers placed above the bit to dampen axial vibration and impact loading, protecting the bit and BHA components and extending run life in hard formations.',
    },
  ];

  return (
    <>
      {/* Hero Section */}
      <div className="container-fluid about-bg d-flex align-items-end" style={{ minHeight: '85vh', padding: 0, top: '-122px' }}>
        <div className="container about-content">
          <div className="row">
            <div className="col-lg-6 col-md-8 col-12 about-text">
              <h1 className="about-title text-white">Downhole Tools</h1>
              <h2 className="about-subtitle">Reaming, Hole Opening &amp; Drilling Dynamics</h2>
              <div className="about-underline" ref={underlineRef}></div>
              <p className="about-desc text-white" style={{ textAlign: 'justify' }}>
                Petrochoice supplies the downhole tools that shape and protect the wellbore — concentric and
                under reamers, hole openers, expandable reamers, roller reamers, and shock subs — selected to
                cut trip count, hold the hole on gauge, and keep the bottom-hole assembly intact.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* What We Offer */}
      <div className="container-xxl py-5">
        <div className="container">
          <div className="row g-5 align-items-center">
            <div className="col-lg-8">
              <h2 className="fw-bold mb-4" style={{ color: '#0c61c0' }}>What We Offer</h2>
              <p className="mb-4">
                Most non-productive time in a drilling programme comes from trips that were not planned for.
                The tools in this range exist to remove those trips: reaming while drilling instead of a
                dedicated wiper run, opening the hole below the shoe without pulling out, and drilling two
                hole sizes in one pass.
              </p>
              <p className="mb-0">
                Alongside the hole-enlargement tools, we supply drilling dynamics equipment — roller reamers
                for stabilisation and shock subs for vibration control — that protect the bit and BHA and
                keep the assembly drilling for longer between trips.
              </p>
            </div>
            <div className="col-lg-4">
              <img
                src={require('../assets/med.png')}
                alt="Downhole Tools"
                className="img-fluid rounded shadow"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Tool Range */}
      <div className="container-xxl py-5" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container">
          <h2 className="fw-bold mb-5 text-center" style={{ color: '#0c61c0' }}>Our Tool Range</h2>
          <div className="row g-4">
            {tools.map((t) => (
              <div className="col-lg-4 col-md-6" key={t.title}>
                <div className="card h-100 border-0 shadow-sm">
                  <div className="card-body p-4">
                    <i className={`${t.icon} fs-2`} style={{ color: t.color }}></i>
                    <h3 className="h6 fw-bold mt-3 mb-2">{t.title}</h3>
                    <p className="mb-0 text-muted small">{t.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* How We Work */}
      <div className="container-xxl py-5">
        <div className="container">
          <div className="row g-5 align-items-center">
            <div className="col-lg-4">
              <img
                src={require('../assets/integrated.jpg')}
                alt="Tool Selection Support"
                className="img-fluid rounded shadow"
              />
            </div>
            <div className="col-lg-8">
              <h2 className="fw-bold mb-4" style={{ color: '#0c61c0' }}>Engineering &amp; Support</h2>
              <p className="mb-4">
                Tool selection is driven by the well plan: casing programme, hole sizes, dogleg severity, and
                the formations to be reamed. We size the tool to the section, confirm activation and pass-through
                requirements against the string, and support the run with performance review afterwards.
              </p>
              <ul className="list-group list-group-flush">
                <li className="list-group-item px-0"><i className="bi bi-diagram-3 text-primary me-2"></i>BHA design and tool placement review</li>
                <li className="list-group-item px-0"><i className="bi bi-arrows-collapse text-success me-2"></i>Pass-through and activation verification</li>
                <li className="list-group-item px-0"><i className="bi bi-graph-up text-info me-2"></i>Post-run performance and wear analysis</li>
                <li className="list-group-item px-0"><i className="bi bi-box-seam text-warning me-2"></i>Rental, servicing, and redress options</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DownholeTools;
